import cx from "clsx"
import { Avatar, Button, Empty, Flex, Icon, Skeleton, Space, Text, Title } from "components/common"
import { IconText, TitleDescription } from "components/custom"
import UserProfileSummary from "features/shared/components/userProfileSummary"
import { memo, useEffect, useRef, useState } from "react"
import { joinBytItems, localisedValue } from "utils"
import { dateTimeFunction, formatRelativeDisplayDateTime } from "utils/date"
import { truncateText } from "utils/reports"
import theme from "utils/themeVars"
import styles from "../index.module.less"
import { startsWithVowel, getLocalizedWhatsAppMessage } from "../utils"
import { t } from "i18next"
const parsedContentType = content => {
  return !!content && JSON.parse(content)
}
const StaffList = memo(
  ({
    staffList,
    selectedStaff,
    onSelectStaff,
    handleLoadButton = () => {},
    isLoadingStaffList = false,
    totalStaffCount = 0,
    client,
    onOpenAnalysis,
   overallEngagementScore,
  }) => {
    const dummyStaffArray = Array.from({ length: 10 }, (_, index) => index)
    const [showEmptyState, setShowEmptyState] = useState(false)
    const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)

    useEffect(() => {
      if (isLoadingStaffList) {
        setShowEmptyState(false)
        setHasInitiallyLoaded(false)
      } else if (!isLoadingStaffList && !staffList?.length) {
        const timer = setTimeout(() => {
          setShowEmptyState(true)
          setHasInitiallyLoaded(true)
        }, 500)
        return () => clearTimeout(timer)
      } else if (!isLoadingStaffList && staffList?.length > 0) {
        setShowEmptyState(false)
        setHasInitiallyLoaded(true)
      }
    }, [isLoadingStaffList, staffList?.length])

    if (isLoadingStaffList && !staffList?.length) {
      return (
        <div className={styles.staffListContainer}>
          <Flex vertical>
            {dummyStaffArray.map((_, index) => (
              <Flex
                key={`transition-skeleton-${index}`}
                justify="space-between"
                className={cx("p-12 pi-20 mbe-16", styles.staffItem)}
              >
                <Flex vertical gap="2px" className="flex-grow">
                  <Skeleton active size="small" width={90} height={12} />
                  <Skeleton active size="small" width={120} height={12} />
                </Flex>
                <Flex vertical align="end">
                  <Skeleton active size="small" width={60} height={12} />
                </Flex>
              </Flex>
            ))}
          </Flex>
        </div>
      )
    }

    if (!isLoadingStaffList && !staffList?.length && showEmptyState && hasInitiallyLoaded) {
      return (
        <div className={styles.staffListContainer}>
          <Flex justify="center" align="center" style={{ height: "100%", minHeight: 300 }} className="flex-grow">
            <Empty description="No Chat History Found" />
          </Flex>
        </div>
      )
    }

    if (!isLoadingStaffList && !staffList?.length && !showEmptyState) {
      return (
        <div className={styles.staffListContainer}>
          <Flex vertical>
            {dummyStaffArray.map((_, index) => (
              <Flex
                key={`transition-skeleton-${index}`}
                justify="space-between"
                className={cx("p-12 pi-20 mbe-16", styles.staffItem)}
              >
                <Flex vertical gap="2px" className="flex-grow">
                  <Skeleton active size="small" width={90} height={12} />
                  <Skeleton active size="small" width={120} height={12} />
                </Flex>
                <Flex vertical align="end">
                  <Skeleton active size="small" width={60} height={12} />
                </Flex>
              </Flex>
            ))}
          </Flex>
        </div>
      )
    }
    return (
      <div className={styles.staffListContainer}>
        <Title level={3} className=" pi-16">
          {t("Staff Members")}
        </Title>

        <Flex vertical>
          <Flex
            justify="space-between"
            className={cx("pointer p-16 mbe-16", styles.staffItem)}
            style={{ minHeight: 72 }}
            onClick={() => {
              if (onOpenAnalysis) onOpenAnalysis()
            }}
          >
            <Flex gap="12px" align="flex-start">
              <Button shape="circle" type="light" icon={"SvgIconAi"} color={theme["accent-color"]} size={30} />

              <Flex vertical gap="4px">
                <Text className="bold fs16">{t("AI Analysis Score:")} {overallEngagementScore}</Text>

                <Text type="secondary" className="fs12">
                  {t("Click to view detailed analysis")}
                </Text>
              </Flex>
            </Flex>
          </Flex>

          {staffList.map((staff, index) => (
            <Flex
              key={`${index}-${staff?.userId}-${staff?.latestMessageId}`}
              justify="space-between"
              className={cx(
                "pointer p-16 mbe-16",
                styles.staffItem,
                selectedStaff?.userId === staff?.userId && selectedStaff?.id === staff?.id && styles.activeStaff
              )}
              onClick={() => onSelectStaff(staff)}
            >
              <Flex gap="12px" align="flex-start" className="flex-grow">
                <Avatar
                  src={staff?.User?.picture}
                  size={40}
                  colorKey={localisedValue({ object: staff?.User, key: "name" })?.[0]?.toLowerCase()}
                >
                  {localisedValue({ object: staff?.User, key: "name" })?.[0]?.toUpperCase()}
                </Avatar>
                <Flex vertical gap="2px" className="flex-grow">
                  <div className="mbe-8">
                    <TitleDescription
                      title={
                        <Space gap="4px">
                          <Text className="bold fs16 fw-medium">
                            {localisedValue({ object: staff?.User, key: "name" })}
                          </Text>
                          {client?.User?.userId === staff?.userId && <Icon icon="BsShare" size="12px" />}
                          <UserProfileSummary staffId={staff?.User?.id} />
                        </Space>
                      }
                      gapSpace={6}
                      description={joinBytItems(
                        [
                          localisedValue({ object: staff?.User?.UserDesignation, key: "short" }),
                          localisedValue({ object: staff?.User?.City, key: "name" }),
                        ],
                        <Icon icon="GoDotFill" size=".7em" color="#D6D6D6" />
                      )}
                    />
                  </div>
                  {!!staff?.LatestMessage && parsedContentType(staff?.LatestMessage?.content)?.type === "text" ? (
                    <IconText
                      icon={staff?.LatestMessage?.sentBy === "staff" ? "FiArrowUpRight" : "FiArrowDownLeft"}
                      iconProps={{ color: "#9d9d9d" }}
                      title={
                        <Text type="secondary">
                          {truncateText(parsedContentType(staff?.LatestMessage?.content)?.message, 60)}
                        </Text>
                      }
                    />
                  ) : (
                    <IconText
                      icon={staff?.LatestMessage?.sentBy === "staff" ? "FiArrowUpRight" : "FiArrowDownLeft"}
                      iconProps={{ color: "#9d9d9d" }}
                      title={
                        <Text type="secondary">{`Sent ${
                          startsWithVowel(parsedContentType(staff?.LatestMessage?.content)?.type) ? "an" : "a"
                        } ${parsedContentType(staff?.LatestMessage?.content)?.type}`}</Text>
                      }
                    />
                  )}
                </Flex>
              </Flex>
              <Text type="secondary" className="fs12 dir-ltr mbe-48">
                {!!staff?.LatestMessage &&
                  formatRelativeDisplayDateTime(
                    dateTimeFunction({ date: staff?.LatestMessage?.messageTimestamp, Default: true })
                  )}
              </Text>
            </Flex>
          ))}

          {isLoadingStaffList && staffList?.length > 0 && (
            <Flex justify="space-between" className={cx("p-12 pi-20 mbe-16", styles.staffItem)}>
              <Flex vertical gap="2px" className="flex-grow">
                <Skeleton active size="small" width={90} height={12} />
                <Skeleton active size="small" width={120} height={12} />
              </Flex>
              <Flex vertical align="end">
                <Skeleton active size="small" width={60} height={12} />
              </Flex>
            </Flex>
          )}

          {totalStaffCount > staffList?.length && !isLoadingStaffList && (
            <div className={"text-center pb-24"}>
              <Button
                iconProps={{ size: "12px" }}
                icon="FaArrowDown"
                loading={isLoadingStaffList}
                text={"Load More"}
                type="light"
                shape="round"
                color={theme["primary-color"]}
                onClick={() => {
                  handleLoadButton()
                }}
              />
            </div>
          )}
        </Flex>
      </div>
    )
  }
)

export default StaffList
